/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { IdentifierKey, isAttributeImmutableIdentifier } from 'cio/utils/identifiers';
import {
  PERMANENT_ATTRIBUTES,
  CREATED_IN_CIO_AT,
  IMMUTABLE_OBJECT_ATTRIBUTES,
  PERMANENT_OBJECT_ATTRIBUTES,
} from 'cio/utils/attributes';
import { AttributeProxy } from 'cio/app-models/customer/attributes-proxy';

interface CioAttributeEditorArgs {
  attribute: AttributeProxy;
  isNew?: boolean;
  showErrors: boolean;
  isSaving: boolean;
  optional?: boolean;
  onKeyChange: () => unknown;
  onRemove: () => unknown;
  isObject?: boolean;
  itemKeyClassNames?: string;
  itemValueClassNames?: string;
}

export default class CioAttributeEditor extends Component<CioAttributeEditorArgs> {
  @service environment!: EnvironmentService;

  @computed('args.attribute.key')
  get isImmutable() {
    if (this.args.isObject === true) {
      return IMMUTABLE_OBJECT_ATTRIBUTES.includes(this.args.attribute.key);
    }

    return (
      this.args.attribute.key === CREATED_IN_CIO_AT ||
      this.args.attribute.key === IdentifierKey.CioId ||
      isAttributeImmutableIdentifier(this.args.attribute.key, this.environment.current.identifiers)
    );
  }

  @computed('isImmutable', 'args.{isNew,attribute.isKeyChanged}')
  get disableInput() {
    return this.isImmutable && this.args.isNew !== true && !this.args.attribute.isKeyChanged;
  }

  @computed('args.attribute.{key,isKeyChanged}')
  get isPermanentAndNotChanged() {
    let { key, isKeyChanged } = this.args.attribute;
    let permanentAttributes = this.args.isObject === true ? PERMANENT_OBJECT_ATTRIBUTES : PERMANENT_ATTRIBUTES;

    return this.isImmutable || (permanentAttributes.includes(key) && !isKeyChanged);
  }

  @computed('args.attribute.key', 'environment.current.isEmailIdentified')
  get isAttributeAutoGenerated() {
    return (
      (this.args.attribute.key === 'id' && this.environment.current.isEmailIdentified) ||
      (this.args.attribute.key === IdentifierKey.CioId &&
        !this.environment.current.allEnabledIdentifiers.includes(IdentifierKey.CioId))
    );
  }

  @computed('args.attribute.key', 'environment.current.isEmailIdentified')
  get isAttributeValidated() {
    return this.args.attribute.key === 'email' && this.environment.current.isEmailIdentified;
  }

  @computed('args.attribute.key')
  get valuePlaceholder() {
    let base = 'Value';

    if (this.args.attribute.key === 'email') {
      base = 'email@domain.com';
    }

    return base;
  }

  @action
  didChangeKey(key: string) {
    this.args.attribute.set('key', key);
    this.args.onKeyChange();
  }

  @action
  didChangeValue(value: string) {
    this.args.attribute.set('value', value);
    this.args.attribute.handleValueChange();
  }
}
